import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';
import LZString from 'lz-string';
import { toast } from 'react-toastify';

import { secondaryApi } from '../../app/utils/ApiUtils';
import { clearSessionStorage, SESSION_CLEAR_DISABLED, isQuotaExceededError } from '../../app/utils/common';
import { publishLog } from '../../app/utils/Logs';
import { metricCheckByService, removeMetricsFromCache } from '../../app/utils/Metrics';
import history from '../../history';
import { authenticate, sessionDataRedirect } from '../session/sessionSlice';

const initialState = {
    data: {},
    error: null, 
    loading: false,
    current: {}
};

export const getMetrics = createAsyncThunk(
    'metric/get',
    async ({key, nocache=true, signal, override=false}, thunkAPI) => {
        const state = thunkAPI.getState()
        const data = {...state.metric.data}

        let answer
        if (!nocache && data[key]) {
            answer = data[key]
            if (answer.ts && Date.now()-answer.ts < 3600*1000) {
                return new Promise((resolve, reject) => resolve({
                    data,
                    current: answer,
                    override
                }))
            }
        }

        const options = {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + state.session.token,
                'X-Client-Version': state.session.clientVersion
            }
        }

        if(signal){
            options.signal = signal
        }

        return fetch(`${secondaryApi}/metrics/${key}`, options)
        .then(async(response) => {
            if(response.ok){

                if(import.meta.env.MODE === "production" && response.headers.get("X-Client-Version") !== state.session.clientVersion){
                    toast.warning("Platform version is not up-to-date. Refreshing now...")

                    setTimeout(() => {
                        window.location.reload()
                    }, 2000)
                }

                let resp = await response.json()
                if(response.name === 'AbortError'){
                    throw response
                }
                let answer = {data: response.ok ? resp.result : {}, dataByMonth: response.ok ? resp.resultMonth : {} ,ts: Date.now()}
                if (!nocache) {
                    const { model } = state?.site
                    if(model?.sites?.length > 0 && model.siteActiveIdx >= 0){
                        const site = model.sites[model.siteActiveIdx]
                        const accts = site?.attr?.accounts ? Object.keys(site?.attr?.accounts) : []
        
                        const metric = resp?.result?.length > 0 ? metricCheckByService(resp.result[0]) : {}
                        
                        const storeInStorage = accts.reduce((prev, curr) => {
                            if(!resp || !resp?.result){
                                prev = false
                            } else {
                                if(metric[curr]){
                                    prev = false
                                }
                            }
        
                            return prev
                        }, true)
        
                        if(storeInStorage){
                            data[key] = answer
                            await thunkAPI.dispatch(setMetricData(data))
                        }
                    }
                }
                
                return {
                    override,
                    data,
                    current: answer
                }
            } else {
                if(response.status === 400 && import.meta.env.MODE === "production"){
                    await publishLog(null, {
                        message: response.statusText,
                        url: window.location.href,
                        siteAccessed: state?.site?.model?.sites?.length > 0 && 
                            state?.site?.model?.siteActiveIdx && 
                            state?.site?.model?.sites[state?.site?.model?.siteActiveIdx] ? 
                            state?.site?.model?.sites[state?.site?.model?.siteActiveIdx] : {},
                        stack: `API request to ${response.url} resulted in: ${response.status} ${response.statusText}`,
                        type: "ERROR"
                    })
                } 
                throw response
            }
        })
        .catch(async(e) => {
            if (e.name === 'AbortError') {
                console.log("Request aborted");
                return {error: e}
            } else if(e.status === 401){

                try {
                    const currSess = await Auth.currentSession()
                    localStorage.setItem('token', currSess.idToken.jwtToken)

                    await thunkAPI.dispatch(authenticate(currSess.idToken.jwtToken))


                    options.headers['Authorization'] = 'Bearer ' + (currSess?.idToken?.jwtToken ?? localStorage.getItem("token"))
    

                    return fetch(`${secondaryApi}/metrics/${key}`, options)
                    .then(async(response) => {
                        if(response.ok){

                            let resp = await response.json()
                            let answer = {data: response.ok ? resp.result : {}, dataByMonth: response.ok ? resp.resultMonth : {} ,ts: Date.now()}
                            if (!nocache) {
                                data[key] = answer
                                await thunkAPI.dispatch(setMetricData(data))
                            }
                            
                            return {
                                override,
                                data,
                                current: answer
                            }
                        } else {
                            throw response
                        }
                    })

                } catch(e) {

                    await Auth.signOut({ global: true })
                    await thunkAPI.dispatch(sessionDataRedirect())
                    localStorage.removeItem('token')
                    window.location.href = '/'
                }
            } else {         
                return {error: e}
            }
        })
})

export const metricSlice = createSlice({
  name: 'metric',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    setMetricData: (state, action) => {
        state.data = action.payload
    },
    setLoading: (state, action) => {
        state.loading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMetrics.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMetrics.fulfilled, (state, action) => {
        if(!action?.payload?.override){
            state.loading = false
        }
        state.data = action?.payload?.data ??  state.data
        state.current = action?.payload?.current ??  state.current
        state.error = action?.payload?.error ??  state.error
      })
      .addCase(getMetrics.rejected, (state, action) => {
        state.loading = false
        state.error = action?.payload?.error ?? state.error
      })

  },
});

export const metric = (state) => state.metric;

export const { setMetricData, setLoading } = metricSlice.actions;

export const deleteMetricSessionStorage = () => async(dispatch, getState) => {
    const state = getState()
    
    // const site = state.site.model.sites[state.site.model.siteActiveIdx]

    await dispatch(setMetricData({}))
    clearSessionStorage()

    // await removeMetricsFromCache(site.siteId)
}

export const setMetricsFromSessionStorage = () => async(dispatch, getState) => {
    let data = {}
    let storeMetrics = true
    for(let key = 0; key < Object.keys(sessionStorage).length; key ++) {
        if(storeMetrics && sessionStorage.key(key).includes("/")){
            let item = JSON.parse(LZString.decompressFromUTF16(sessionStorage.getItem(sessionStorage.key(key))))
            data[sessionStorage.key(key)] = item
            if (item && item?.ts && Date.now()-item?.ts > 3600*1000) {
                data = {}
                storeMetrics = false
                break 
            }
        }
    }
    if(storeMetrics){
        dispatch(setMetricData(data))
    } else {
        clearSessionStorage()
    }
}

export default metricSlice.reducer;
